/**
 * Content Chunking Utility - Enhanced Semantic Version
 * 
 * Processes raw scraped data and intelligently chunks content for optimal embedding generation.
 * Handles content cleaning, categorization, and smart chunking with overlap for context preservation.
 * 
 * Enhanced Features:
 * - Semantic chunking that preserves names and dates
 * - Biographical entry preservation
 * - Sentence-boundary based splitting
 * - Structured metadata extraction
 * - Name variant generation for better search
 */

export interface RawScrapedData {
  url: string;
  title: string;
  content: string;
  timestamp: string;
}

export interface ContentChunk {
  content: string;
  metadata: {
    source_url: string;
    title: string;
    category: string;
    chunk_index: number;
    total_chunks: number;
    original_timestamp: string;
    processed_at: string;
    token_count: number;
    type: 'policy' | 'service' | 'location' | 'general' | 'news' | 'history' | 'biography' | 'faq';
    source: string;
    content_type: 'biography' | 'faq' | 'service' | 'policy' | 'location' | 'general';
    canonical_name?: string;
    name_variants?: string[];
    birth_year?: number;
    death_year?: number;
    entities_preserved?: string[];
    sentence_count: number;
    overlap_with_previous?: boolean;
    [key: string]: any;
  };
}

export interface ChunkingOptions {
  targetTokens?: number;
  overlapPercentage?: number;
  minTokens?: number;
  maxTokens?: number;
  preserveEntities?: boolean;
  useSentenceBoundaries?: boolean;
}

/**
 * Enhanced token estimation with better accuracy for cemetery content
 */
function estimateTokenCount(text: string): number {
  // More accurate token estimation for cemetery/biographical content
  const words = text.trim().split(/\s+/);
  const characters = text.length;
  
  // Account for names, dates, and cemetery-specific terminology
  const nameMatches = text.match(/[A-Z][a-z]+ [A-Z][a-z]+/g) || [];
  const dateMatches = text.match(/\b\d{4}\b|\b\d{1,2}\/\d{1,2}\/\d{4}\b/g) || [];
  const cemeteryTerms = text.match(/\b(cemetery|memorial|burial|cremation|grave|plot|section)\b/gi) || [];
  
  // Adjust token estimation based on content type
  let tokenMultiplier = 1.3; // Base multiplier
  
  // Names and dates are typically 1 token each
  tokenMultiplier += (nameMatches.length * 0.1);
  tokenMultiplier += (dateMatches.length * 0.05);
  tokenMultiplier += (cemeteryTerms.length * 0.05);
  
  const wordBasedTokens = words.length * tokenMultiplier;
  const charBasedTokens = characters / 4;
  
  return Math.round((wordBasedTokens + charBasedTokens) / 2);
}

/**
 * Extract names from text with variants
 */
function extractNames(text: string): {
  canonical_name?: string;
  name_variants?: string[];
  birth_year?: number;
  death_year?: number;
} {
  const result: any = {};
  
  // Pattern for full names with optional dates: "John Smith (1924-1963)"
  const nameWithDatesPattern = /([A-Z][a-z]+ (?:[A-Z][a-z]+ )*[A-Z][a-z]+)\s*\((\d{4})-(\d{4})\)/g;
  const nameWithDatesMatch = nameWithDatesPattern.exec(text);
  
  if (nameWithDatesMatch) {
    const fullName = nameWithDatesMatch[1].trim();
    result.canonical_name = fullName;
    result.birth_year = parseInt(nameWithDatesMatch[2]);
    result.death_year = parseInt(nameWithDatesMatch[3]);
    
    // Generate name variants
    result.name_variants = generateNameVariants(fullName);
    
    return result;
  }
  
  // Pattern for names without dates
  const namePattern = /\b([A-Z][a-z]+ (?:[A-Z][a-z]+ )*[A-Z][a-z]+)\b/g;
  const nameMatches = text.match(namePattern);
  
  if (nameMatches && nameMatches.length > 0) {
    // Use the first substantial name found
    const potentialNames = nameMatches.filter(name => 
      name.split(' ').length >= 2 && 
      !['Memorial Park', 'Cemetery Trust', 'Greater Metropolitan'].includes(name)
    );
    
    if (potentialNames.length > 0) {
      result.canonical_name = potentialNames[0];
      result.name_variants = generateNameVariants(potentialNames[0]);
    }
  }
  
  // Extract years separately if no full pattern matched
  if (!result.birth_year || !result.death_year) {
    const yearPattern = /\b(19|20)\d{2}\b/g;
    const years = text.match(yearPattern);
    if (years && years.length >= 2) {
      const sortedYears = years.map(y => parseInt(y)).sort();
      if (!result.birth_year) result.birth_year = sortedYears[0];
      if (!result.death_year) result.death_year = sortedYears[sortedYears.length - 1];
    }
  }
  
  return result;
}

/**
 * Generate name variants for better search matching
 */
function generateNameVariants(fullName: string): string[] {
  const variants = new Set<string>();
  
  // Add original name
  variants.add(fullName);
  
  // Add lowercase version
  variants.add(fullName.toLowerCase());
  
  // Add uppercase version
  variants.add(fullName.toUpperCase());
  
  // Add first name + last name initial
  const nameParts = fullName.split(' ');
  if (nameParts.length >= 2) {
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    
    // "John Smith" → "J. Smith", "John S.", "J.Smith"
    variants.add(`${firstName.charAt(0)}. ${lastName}`);
    variants.add(`${firstName} ${lastName.charAt(0)}.`);
    variants.add(`${firstName.charAt(0)}.${lastName}`);
    
    // Add reversed order: "Smith, John"
    variants.add(`${lastName}, ${firstName}`);
    
    // Add middle initial handling if present
    if (nameParts.length === 3) {
      const middleName = nameParts[1];
      variants.add(`${firstName} ${middleName.charAt(0)}. ${lastName}`);
      variants.add(`${firstName} ${middleName} ${lastName}`);
    }
  }
  
  return Array.from(variants);
}

/**
 * Detect if content is a biographical entry
 */
function isBiographicalEntry(text: string): boolean {
  const bioIndicators = [
    /\b\d{4}-\d{4}\b/, // Birth-death years
    /\bborn\b/i,
    /\bdied\b/i,
    /\bpassed away\b/i,
    /\bburied\b/i,
    /\bmemorial\b/i,
    /\bsurvived by\b/i,
    /\bbeloved\b/i,
    /\bfuneral\b/i
  ];
  
  return bioIndicators.some(pattern => pattern.test(text));
}

/**
 * Detect content type based on patterns and keywords
 */
function detectContentType(text: string): 'biography' | 'faq' | 'service' | 'policy' | 'location' | 'general' {
  const lowerText = text.toLowerCase();
  
  // Biography detection
  if (isBiographicalEntry(text)) {
    return 'biography';
  }
  
  // FAQ detection
  if (lowerText.includes('q:') || lowerText.includes('question:') || 
      lowerText.includes('a:') || lowerText.includes('answer:') ||
      lowerText.match(/^(what|how|when|where|why|can|do|does|is|are)/)) {
    return 'faq';
  }
  
  // Service detection
  if (lowerText.includes('service') || lowerText.includes('burial') ||
      lowerText.includes('cremation') || lowerText.includes('memorial') ||
      lowerText.includes('funeral') || lowerText.includes('ceremony')) {
    return 'service';
  }
  
  // Policy detection
  if (lowerText.includes('policy') || lowerText.includes('rule') ||
      lowerText.includes('regulation') || lowerText.includes('guideline') ||
      lowerText.includes('permitted') || lowerText.includes('prohibited')) {
    return 'policy';
  }
  
  // Location detection
  if (lowerText.includes('location') || lowerText.includes('address') ||
      lowerText.includes('directions') || lowerText.includes('hours') ||
      lowerText.includes('cemetery') || lowerText.includes('memorial park')) {
    return 'location';
  }
  
  return 'general';
}

/**
 * Extract entities that should be preserved (names, dates, places)
 */
function extractPreservableEntities(text: string): string[] {
  const entities = new Set<string>();
  
  // Extract full names
  const namePattern = /\b[A-Z][a-z]+ (?:[A-Z][a-z]+ )*[A-Z][a-z]+\b/g;
  const names = text.match(namePattern) || [];
  names.forEach(name => {
    if (!['Memorial Park', 'Cemetery Trust', 'Greater Metropolitan'].includes(name)) {
      entities.add(name);
    }
  });
  
  // Extract dates
  const datePatterns = [
    /\b\d{1,2}\/\d{1,2}\/\d{4}\b/g, // MM/DD/YYYY
    /\b\d{4}-\d{4}\b/g, // YYYY-YYYY
    /\b\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/gi,
    /\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\b/gi
  ];
  
  datePatterns.forEach(pattern => {
    const dates = text.match(pattern) || [];
    dates.forEach(date => entities.add(date));
  });
  
  // Extract cemetery locations
  const locationPattern = /\b(?:Altona|Fawkner|Emerald|Memorial Park|Cemetery)\b/gi;
  const locations = text.match(locationPattern) || [];
  locations.forEach(location => entities.add(location));
  
  // Extract plot/section references
  const plotPattern = /\b(?:Plot|Section|Row)\s+[A-Z0-9-]+\b/gi;
  const plots = text.match(plotPattern) || [];
  plots.forEach(plot => entities.add(plot));
  
  return Array.from(entities);
}

/**
 * Find sentence boundaries in text
 */
function findSentenceBoundaries(text: string): number[] {
  const boundaries = [0]; // Start of text
  
  // Enhanced sentence boundary detection
  const sentencePattern = /[.!?]+\s+(?=[A-Z])/g;
  let match;
  
  while ((match = sentencePattern.exec(text)) !== null) {
    const boundaryPos = match.index + match[0].length;
    boundaries.push(boundaryPos);
  }
  
  // Add end of text
  if (boundaries[boundaries.length - 1] !== text.length) {
    boundaries.push(text.length);
  }
  
  return boundaries;
}

/**
 * Check if splitting at a position would break an entity
 */
function wouldBreakEntity(text: string, position: number, entities: string[]): boolean {
  const contextWindow = 50; // Characters to check around the position
  const start = Math.max(0, position - contextWindow);
  const end = Math.min(text.length, position + contextWindow);
  const contextText = text.substring(start, end);
  
  // Check if any entity spans across the split position
  return entities.some(entity => {
    const entityStart = contextText.indexOf(entity);
    if (entityStart === -1) return false;
    
    const entityEnd = entityStart + entity.length;
    const relativePosition = position - start;
    
    // Entity would be split if position falls within entity bounds
    return relativePosition > entityStart && relativePosition < entityEnd;
  });
}

/**
 * Semantic chunking that preserves entities and uses sentence boundaries
 */
function semanticChunkContent(
  content: string,
  options: ChunkingOptions = {}
): string[] {
  const {
    targetTokens = 400,
    overlapPercentage = 17.5, // 15-20% overlap
    minTokens = 200,
    maxTokens = 500,
    preserveEntities = true,
    useSentenceBoundaries = true
  } = options;
  
  // Check if content is small enough to keep as single chunk
  const totalTokens = estimateTokenCount(content);
  if (totalTokens <= maxTokens) {
    return [content];
  }
  
  // For biographical entries under 100 tokens, preserve as single chunk
  if (isBiographicalEntry(content) && totalTokens <= 100) {
    return [content];
  }
  
  // Extract entities to preserve
  const entities = preserveEntities ? extractPreservableEntities(content) : [];
  
  // Find sentence boundaries
  const sentenceBoundaries = useSentenceBoundaries ? findSentenceBoundaries(content) : [];
  
  const chunks: string[] = [];
  let currentPosition = 0;
  const contentLength = content.length;
  
  // Calculate overlap in characters
  const avgCharsPerToken = content.length / totalTokens;
  const targetChars = Math.round(targetTokens * avgCharsPerToken);
  const overlapChars = Math.round(targetChars * (overlapPercentage / 100));
  
  while (currentPosition < contentLength) {
    let endPosition = Math.min(currentPosition + targetChars, contentLength);
    
    // Try to end at a sentence boundary
    if (useSentenceBoundaries && endPosition < contentLength) {
      const nearestBoundary = sentenceBoundaries.find(boundary => 
        boundary >= endPosition - (targetChars * 0.2) && boundary <= endPosition + (targetChars * 0.2)
      );
      
      if (nearestBoundary && nearestBoundary > currentPosition + (targetChars * 0.7)) {
        endPosition = nearestBoundary;
      }
    }
    
    // Check if we would break any entities
    if (preserveEntities && endPosition < contentLength) {
      if (wouldBreakEntity(content, endPosition, entities)) {
        // Find a safe boundary that doesn't break entities
        let safePosition = endPosition;
        
        // Try sentence boundaries first
        for (let i = sentenceBoundaries.length - 1; i >= 0; i--) {
          const boundary = sentenceBoundaries[i];
          if (boundary < endPosition && boundary > currentPosition + (targetChars * 0.5)) {
            if (!wouldBreakEntity(content, boundary, entities)) {
              safePosition = boundary;
              break;
            }
          }
        }
        
        // If no safe sentence boundary, find word boundary
        if (safePosition === endPosition) {
          const wordBoundaryPattern = /\s+/g;
          let match;
          wordBoundaryPattern.lastIndex = endPosition - 100; // Start looking 100 chars back
          
          while ((match = wordBoundaryPattern.exec(content)) !== null) {
            if (match.index < endPosition && match.index > currentPosition + (targetChars * 0.5)) {
              if (!wouldBreakEntity(content, match.index, entities)) {
                safePosition = match.index;
              }
            }
            if (match.index >= endPosition) break;
          }
        }
        
        endPosition = safePosition;
      }
    }
    
    // Extract chunk
    const chunk = content.substring(currentPosition, endPosition).trim();
    
    // Validate chunk size
    const chunkTokens = estimateTokenCount(chunk);
    if (chunkTokens >= minTokens || chunks.length === 0) {
      chunks.push(chunk);
    }
    
    // Calculate next position with overlap
    if (endPosition >= contentLength) {
      break;
    }
    
    // Calculate overlap start position
    let nextPosition = endPosition - overlapChars;
    
    // Try to start overlap at a sentence boundary
    if (useSentenceBoundaries) {
      const overlapBoundary = sentenceBoundaries.find(boundary => 
        boundary >= nextPosition - (overlapChars * 0.5) && 
        boundary <= nextPosition + (overlapChars * 0.5) &&
        boundary < endPosition
      );
      
      if (overlapBoundary) {
        nextPosition = overlapBoundary;
      }
    }
    
    // Ensure we make progress
    currentPosition = Math.max(nextPosition, currentPosition + 1);
  }
  
  return chunks;
}

/**
 * Enhanced content cleaning with entity preservation
 */
function cleanContentPreservingEntities(content: string): string {
  return content
    // Remove "Home/" prefixes but preserve entity context
    .replace(/^Home\/+/gi, '')
    .replace(/\bHome\/+(?![A-Z][a-z]+ [A-Z])/g, '') // Don't remove if followed by a name
    
    // Normalize whitespace but preserve intentional formatting around names/dates
    .replace(/\s+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    
    // Clean up punctuation but preserve dates and names
    .replace(/\s+([,.!?;:])/g, '$1')
    .replace(/([,.!?;:])\s+/g, '$1 ')
    
    // Preserve name formatting: "John Smith (1924-1963)"
    .replace(/\(\s*(\d{4})\s*-\s*(\d{4})\s*\)/g, '($1-$2)')
    
    // Clean up common web artifacts
    .replace(/\s*\|\s*/g, ' | ')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s*&\s*/g, ' & ')
    
    // Remove multiple consecutive spaces
    .replace(/  +/g, ' ')
    
    // Trim and ensure clean ending
    .trim();
}

/**
 * Enhanced category detection with biographical content
 */
function detectEnhancedCategory(url: string, title: string, content: string): {
  category: string;
  type: 'policy' | 'service' | 'location' | 'general' | 'news' | 'history' | 'biography' | 'faq';
  content_type: 'biography' | 'faq' | 'service' | 'policy' | 'location' | 'general';
} {
  const urlLower = url.toLowerCase();
  const titleLower = title.toLowerCase();
  const contentLower = content.toLowerCase();
  const combinedText = `${urlLower} ${titleLower} ${contentLower}`;
  
  // Biography detection (highest priority)
  if (isBiographicalEntry(content)) {
    return { category: 'biography', type: 'biography', content_type: 'biography' };
  }
  
  // FAQ detection
  if (content.includes('Q:') || content.includes('A:') || 
      titleLower.includes('faq') || titleLower.includes('question') ||
      content.match(/^(what|how|when|where|why|can|do|does|is|are)/i)) {
    return { category: 'faq', type: 'faq', content_type: 'faq' };
  }
  
  // History and Heritage
  if (urlLower.includes('/history-heritage-hub/') || 
      combinedText.includes('history') || combinedText.includes('heritage') ||
      combinedText.includes('established') || combinedText.includes('founded')) {
    return { category: 'history', type: 'history', content_type: 'general' };
  }
  
  // Locations
  if (urlLower.includes('/our-locations/') || 
      combinedText.includes('location') || combinedText.includes('address') ||
      combinedText.includes('cemetery') || combinedText.includes('memorial park') ||
      combinedText.includes('directions') || combinedText.includes('visiting hours')) {
    return { category: 'location', type: 'location', content_type: 'location' };
  }
  
  // Services
  if (combinedText.includes('service') || combinedText.includes('burial') ||
      combinedText.includes('cremation') || combinedText.includes('memorial') ||
      combinedText.includes('funeral') || combinedText.includes('pre-planning') ||
      combinedText.includes('ceremony') || combinedText.includes('chapel')) {
    return { category: 'services', type: 'service', content_type: 'service' };
  }
  
  // Policies and Rules
  if (combinedText.includes('policy') || combinedText.includes('rule') ||
      combinedText.includes('regulation') || combinedText.includes('guideline') ||
      combinedText.includes('permitted') || combinedText.includes('prohibited') ||
      combinedText.includes('terms') || combinedText.includes('conditions')) {
    return { category: 'policy', type: 'policy', content_type: 'policy' };
  }
  
  // Default to general
  return { category: 'general', type: 'general', content_type: 'general' };
}

/**
 * Enhanced metadata extraction with structured data
 */
function extractEnhancedMetadata(content: string, url: string, title: string): Record<string, any> {
  const metadata: Record<string, any> = {};
  
  // Extract name information
  const nameInfo = extractNames(content);
  Object.assign(metadata, nameInfo);
  
  // Extract entities that were preserved
  metadata.entities_preserved = extractPreservableEntities(content);
  
  // Extract phone numbers (Australian format)
  const phoneRegex = /(?:\(0[2-8]\)\s?\d{4}\s?\d{4}|\+61\s?[2-8]\s?\d{4}\s?\d{4}|0[2-8]\s?\d{4}\s?\d{4})/g;
  const phones = content.match(phoneRegex);
  if (phones && phones.length > 0) {
    metadata.phone_numbers = [...new Set(phones)];
  }
  
  // Extract email addresses
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  const emails = content.match(emailRegex);
  if (emails && emails.length > 0) {
    metadata.email_addresses = [...new Set(emails)];
  }
  
  // Extract Australian addresses
  const addressRegex = /\d+\s+[A-Za-z\s]+(?:Street|St|Road|Rd|Avenue|Ave|Drive|Dr|Lane|Ln|Court|Ct|Place|Pl|Way|Crescent|Cres|Boulevard|Blvd),?\s+[A-Za-z\s]+\s+(?:VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\s+\d{4}/g;
  const addresses = content.match(addressRegex);
  if (addresses && addresses.length > 0) {
    metadata.addresses = [...new Set(addresses)];
  }
  
  // Extract operating hours
  const hoursRegex = /\d{1,2}(?::\d{2})?\s*(?:AM|PM)\s*[-–—]\s*\d{1,2}(?::\d{2})?\s*(?:AM|PM)/gi;
  const hours = content.match(hoursRegex);
  if (hours && hours.length > 0) {
    metadata.operating_hours = [...new Set(hours)];
  }
  
  // Extract prices (Australian currency)
  const priceRegex = /\$[\d,]+(?:\.\d{2})?/g;
  const prices = content.match(priceRegex);
  if (prices && prices.length > 0) {
    metadata.prices = [...new Set(prices)];
  }
  
  // Extract plot/section references
  const plotRegex = /(?:Plot|Section|Row)\s+[A-Z0-9-]+/gi;
  const plots = content.match(plotRegex);
  if (plots && plots.length > 0) {
    metadata.plot_references = [...new Set(plots)];
  }
  
  // URL analysis
  try {
    const urlObj = new URL(url);
    metadata.domain = urlObj.hostname;
    metadata.url_path = urlObj.pathname;
    metadata.url_params = urlObj.search;
  } catch {
    // Invalid URL, skip URL analysis
  }
  
  // Content analysis
  metadata.content_length = content.length;
  metadata.word_count = content.trim().split(/\s+/).length;
  metadata.sentence_count = (content.match(/[.!?]+/g) || []).length;
  
  return metadata;
}

/**
 * Main function to process raw scraped data into optimized semantic chunks
 */
export function chunkScrapedContent(
  rawData: RawScrapedData[],
  options: ChunkingOptions = {}
): ContentChunk[] {
  const allChunks: ContentChunk[] = [];
  
  console.log(`🔄 Processing ${rawData.length} scraped items with semantic chunking`);
  
  rawData.forEach((item, itemIndex) => {
    try {
      // Clean the content while preserving entities
      const cleanedContent = cleanContentPreservingEntities(item.content);
      
      // Skip if content is too short after cleaning
      if (cleanedContent.length < 50) {
        console.warn(`⚠️ Skipping item ${itemIndex + 1}: Content too short after cleaning`);
        return;
      }
      
      // Detect category, type, and content type
      const { category, type, content_type } = detectEnhancedCategory(item.url, item.title, cleanedContent);
      
      // Extract enhanced metadata
      const extractedMetadata = extractEnhancedMetadata(cleanedContent, item.url, item.title);
      
      // Create content for chunking (combine title and content)
      const fullContent = `${item.title}\n\n${cleanedContent}`.trim();
      
      // Use semantic chunking
      const contentChunks = semanticChunkContent(fullContent, options);
      
      console.log(`📄 Item ${itemIndex + 1}: "${item.title}" → ${contentChunks.length} semantic chunks`);
      
      // Create chunk objects with enhanced metadata
      contentChunks.forEach((chunkContent, chunkIndex) => {
        const tokenCount = estimateTokenCount(chunkContent);
        const sentenceCount = (chunkContent.match(/[.!?]+/g) || []).length;
        
        // Extract chunk-specific name information
        const chunkNameInfo = extractNames(chunkContent);
        
        const chunk: ContentChunk = {
          content: chunkContent,
          metadata: {
            source_url: item.url,
            title: item.title,
            category,
            chunk_index: chunkIndex + 1,
            total_chunks: contentChunks.length,
            original_timestamp: item.timestamp,
            processed_at: new Date().toISOString(),
            token_count: tokenCount,
            type,
            source: 'web_scraper',
            content_type,
            sentence_count: sentenceCount,
            overlap_with_previous: chunkIndex > 0,
            original_item_index: itemIndex + 1,
            
            // Enhanced metadata
            ...extractedMetadata,
            
            // Chunk-specific name information (may differ from item-level)
            ...chunkNameInfo,
            
            // Semantic chunking metadata
            chunking_method: 'semantic',
            entities_preserved: extractPreservableEntities(chunkContent),
            is_biographical: isBiographicalEntry(chunkContent),
            overlap_percentage: options.overlapPercentage || 17.5
          }
        };
        
        allChunks.push(chunk);
      });
      
    } catch (error) {
      console.error(`❌ Error processing item ${itemIndex + 1}:`, error);
    }
  });
  
  console.log(`✅ Semantic chunking complete: ${allChunks.length} total chunks from ${rawData.length} items`);
  
  // Log semantic chunking statistics
  const stats = getSemanticChunkingStats(allChunks);
  console.log('📊 Semantic Chunking Statistics:', stats);
  
  return allChunks;
}

/**
 * Get comprehensive statistics about semantic chunking results
 */
export function getSemanticChunkingStats(chunks: ContentChunk[]): {
  totalChunks: number;
  biographicalChunks: number;
  chunksWithNames: number;
  avgTokensPerChunk: number;
  avgSentencesPerChunk: number;
  overlapUtilization: number;
  contentTypeDistribution: Record<string, number>;
  entityPreservationRate: number;
  nameVariantCoverage: number;
} {
  const biographicalChunks = chunks.filter(c => c.metadata.is_biographical).length;
  const chunksWithNames = chunks.filter(c => c.metadata.canonical_name).length;
  const chunksWithOverlap = chunks.filter(c => c.metadata.overlap_with_previous).length;
  const chunksWithEntities = chunks.filter(c => c.metadata.entities_preserved?.length > 0).length;
  const chunksWithNameVariants = chunks.filter(c => c.metadata.name_variants?.length > 0).length;
  
  const tokenCounts = chunks.map(c => c.metadata.token_count);
  const sentenceCounts = chunks.map(c => c.metadata.sentence_count);
  
  const contentTypeDistribution: Record<string, number> = {};
  chunks.forEach(chunk => {
    const contentType = chunk.metadata.content_type;
    contentTypeDistribution[contentType] = (contentTypeDistribution[contentType] || 0) + 1;
  });
  
  return {
    totalChunks: chunks.length,
    biographicalChunks,
    chunksWithNames,
    avgTokensPerChunk: tokenCounts.length > 0 
      ? Math.round(tokenCounts.reduce((a, b) => a + b, 0) / tokenCounts.length)
      : 0,
    avgSentencesPerChunk: sentenceCounts.length > 0
      ? Math.round(sentenceCounts.reduce((a, b) => a + b, 0) / sentenceCounts.length * 10) / 10
      : 0,
    overlapUtilization: chunks.length > 0 ? (chunksWithOverlap / chunks.length) * 100 : 0,
    contentTypeDistribution,
    entityPreservationRate: chunks.length > 0 ? (chunksWithEntities / chunks.length) * 100 : 0,
    nameVariantCoverage: chunksWithNames > 0 ? (chunksWithNameVariants / chunksWithNames) * 100 : 0
  };
}

/**
 * Validates semantic chunks meet quality requirements
 */
export function validateSemanticChunks(chunks: ContentChunk[], options: ChunkingOptions = {}): {
  valid: boolean;
  issues: string[];
  stats: {
    totalChunks: number;
    validChunks: number;
    invalidChunks: number;
    avgTokens: number;
    minTokens: number;
    maxTokens: number;
    entitiesPreserved: number;
    biographicalIntegrity: number;
  };
} {
  const { minTokens = 200, maxTokens = 500 } = options;
  const issues: string[] = [];
  let validCount = 0;
  let entitiesPreservedCount = 0;
  let biographicalIntegrityCount = 0;
  
  const tokenCounts = chunks.map(chunk => chunk.metadata.token_count);
  
  chunks.forEach((chunk, index) => {
    const tokens = chunk.metadata.token_count;
    
    // Token count validation
    if (tokens < minTokens) {
      issues.push(`Chunk ${index + 1}: Too few tokens (${tokens} < ${minTokens})`);
    } else if (tokens > maxTokens) {
      issues.push(`Chunk ${index + 1}: Too many tokens (${tokens} > ${maxTokens})`);
    } else {
      validCount++;
    }
    
    // Content validation
    if (!chunk.content.trim()) {
      issues.push(`Chunk ${index + 1}: Empty content`);
    }
    
    // Metadata validation
    if (!chunk.metadata.source_url || !chunk.metadata.title) {
      issues.push(`Chunk ${index + 1}: Missing required metadata`);
    }
    
    // Entity preservation validation
    if (chunk.metadata.entities_preserved && chunk.metadata.entities_preserved.length > 0) {
      entitiesPreservedCount++;
    }
    
    // Biographical integrity validation
    if (chunk.metadata.is_biographical && chunk.metadata.canonical_name) {
      biographicalIntegrityCount++;
    }
  });
  
  const stats = {
    totalChunks: chunks.length,
    validChunks: validCount,
    invalidChunks: chunks.length - validCount,
    avgTokens: tokenCounts.length > 0 ? Math.round(tokenCounts.reduce((a, b) => a + b, 0) / tokenCounts.length) : 0,
    minTokens: tokenCounts.length > 0 ? Math.min(...tokenCounts) : 0,
    maxTokens: tokenCounts.length > 0 ? Math.max(...tokenCounts) : 0,
    entitiesPreserved: entitiesPreservedCount,
    biographicalIntegrity: biographicalIntegrityCount
  };
  
  return {
    valid: issues.length === 0,
    issues,
    stats
  };
}

/**
 * Utility function to preview semantic chunking results
 */
export function previewSemanticChunking(
  rawData: RawScrapedData[],
  options: ChunkingOptions = {}
): {
  estimatedChunks: number;
  biographicalEntries: number;
  itemBreakdown: Array<{
    title: string;
    url: string;
    originalTokens: number;
    estimatedChunks: number;
    category: string;
    type: string;
    content_type: string;
    hasNames: boolean;
    isBiographical: boolean;
    entitiesFound: number;
  }>;
} {
  let totalEstimatedChunks = 0;
  let biographicalEntries = 0;
  const itemBreakdown: any[] = [];
  
  rawData.forEach(item => {
    const cleanedContent = cleanContentPreservingEntities(item.content);
    const fullContent = `${item.title}\n\n${cleanedContent}`;
    const tokens = estimateTokenCount(fullContent);
    const { category, type, content_type } = detectEnhancedCategory(item.url, item.title, cleanedContent);
    const nameInfo = extractNames(fullContent);
    const entities = extractPreservableEntities(fullContent);
    const isBio = isBiographicalEntry(fullContent);
    
    if (isBio) biographicalEntries++;
    
    // For biographical entries under 100 tokens, count as 1 chunk
    const estimatedChunks = (isBio && tokens <= 100) ? 1 : 
      Math.max(1, Math.ceil(tokens / (options.targetTokens || 400)));
    
    totalEstimatedChunks += estimatedChunks;
    
    itemBreakdown.push({
      title: item.title,
      url: item.url,
      originalTokens: tokens,
      estimatedChunks,
      category,
      type,
      content_type,
      hasNames: !!nameInfo.canonical_name,
      isBiographical: isBio,
      entitiesFound: entities.length
    });
  });
  
  return {
    estimatedChunks: totalEstimatedChunks,
    biographicalEntries,
    itemBreakdown
  };
}

/**
 * Export utility functions for external use
 */
export const semanticChunkingUtils = {
  estimateTokenCount,
  extractNames,
  generateNameVariants,
  isBiographicalEntry,
  detectContentType,
  extractPreservableEntities,
  findSentenceBoundaries,
  wouldBreakEntity,
  cleanContentPreservingEntities,
  detectEnhancedCategory,
  extractEnhancedMetadata,
  validateSemanticChunks,
  getSemanticChunkingStats,
  previewSemanticChunking
};

/**
 * Legacy compatibility - keep original function names
 */
export const chunkingUtils = {
  estimateTokenCount,
  cleanContent: cleanContentPreservingEntities,
  detectCategory: detectEnhancedCategory,
  extractMetadata: extractEnhancedMetadata,
  validateChunks: validateSemanticChunks,
  getChunkingStats: getSemanticChunkingStats,
  previewChunking: previewSemanticChunking
};

/**
 * Default export for easy importing
 */
export default {
  chunkScrapedContent,
  semanticChunkingUtils,
  chunkingUtils,
  validateSemanticChunks,
  getSemanticChunkingStats,
  previewSemanticChunking
};